import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CalendarDays } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHADE = ["bg-slate-100 text-slate-400", "bg-emerald-100 text-emerald-800", "bg-emerald-300 text-emerald-900", "bg-emerald-500 text-white", "bg-emerald-700 text-white"];

function pmEntries(pm) {
  const out = [];
  Object.entries(pm || {}).forEach(([spec, v]) => {
    const arr = Array.isArray(v) ? v : (v && v.provider_id ? [v] : []);
    arr.forEach(e => e && e.provider_id && out.push({ spec, ...e }));
  });
  return out;
}

export default function CoverageHeatmap({ facility, compact = false }) {
  const [providers, setProviders] = useState([]);
  useEffect(() => { api.get("/providers").then(({ data }) => setProviders(data)).catch(() => {}); }, []);
  const grid = useMemo(() => {
    const live = new Map(providers.map(p => [p.provider_id, p]));
    const attached = pmEntries(facility?.providers_map).map(e => ({ ...e, ...(live.get(e.provider_id) || {}) })).filter(e => e.active !== false);
    return DAYS.map(d => {
      const who = attached.filter(e => (e.available_days || []).includes(d));
      return { day: d, count: who.length, names: who.map(w => `${w.name} (${w.specialty || w.spec})`) };
    });
  }, [facility, providers]);
  const total = pmEntries(facility?.providers_map).length;
  const gaps = grid.filter(g => g.count === 0 && !["Sat", "Sun"].includes(g.day)).map(g => g.day);
  const body = (
    <div className="grid grid-cols-7 gap-1.5" data-testid="coverage-heatmap">
      {grid.map(g => (
        <div key={g.day} title={g.names.join("\n") || "No provider available"} className={`rounded-md ${compact ? "py-1.5" : "py-3"} text-center ${SHADE[Math.min(g.count, 4)]} transition-colors`} data-testid={`coverage-${g.day}`}>
          <div className="text-[9px] uppercase tracking-widest font-mono-plex opacity-80">{g.day}</div>
          <div className={`font-heading font-black ${compact ? "text-base" : "text-xl"} leading-tight`}>{g.count}</div>
        </div>
      ))}
    </div>
  );
  if (compact) return body;
  return (
    <Card className="p-5 border-slate-200 shadow-none" data-testid="coverage-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Weekly coverage</div>
          <h3 className="font-heading font-bold text-lg">Providers available per day</h3>
        </div>
        <div className="text-xs text-slate-500 font-mono-plex text-right">{total} attached{gaps.length > 0 && <div className="text-rose-600" data-testid="coverage-gaps">No weekday coverage: {gaps.join(", ")}</div>}</div>
      </div>
      {body}
      <p className="text-[11px] text-slate-400 mt-2">Hover a day to see who covers it. Based on each provider's available days.</p>
    </Card>
  );
}
